import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../hooks/useRole';
import { cn } from '../lib/utils';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import Breadcrumbs from './Breadcrumbs';
import DefaultPasswordBanner from './DefaultPasswordBanner';
import { motion, AnimatePresence } from 'framer-motion';

const {
  LuLayoutDashboard, LuUsers, LuQrCode, LuReceipt, LuLogOut,
  LuGraduationCap, LuMoon, LuSun, LuMenu, LuX, LuTrendingUp,
  LuShieldCheck, LuSettings, LuUserCog,
} = LuIcons;

export default function Layout() {
  const { signOut } = useAuth();
  const { isOwner, profile } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('theme') === 'dark');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const baseNavItems = [
    { icon: LuLayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: LuTrendingUp, label: 'Performance', path: '/analytics' },
    { icon: LuUsers, label: 'Students', path: '/students' },
    { icon: LuQrCode, label: 'Scanner', path: '/scanner' },
    { icon: LuReceipt, label: 'Invoices', path: '/invoices' },
    { icon: LuShieldCheck, label: 'Admin Center', path: '/admin' },
    { icon: LuSettings, label: 'Settings', path: '/settings' },
  ];

  // Only inject User Management for owners
  const navItems = isOwner
    ? [
        ...baseNavItems.slice(0, 6),
        { icon: LuUserCog, label: 'User Mgmt', path: '/users', ownerOnly: true },
        baseNavItems[6],
      ]
    : baseNavItems;

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => { setIsSidebarOpen(false); }, [location.pathname]);

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      {/* Default Password Banner */}
      <DefaultPasswordBanner />

      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 md:hidden"
          />
        )}
      </AnimatePresence>

      <aside className={cn(
        "fixed inset-y-0 left-0 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-50 transition-transform duration-300 transform md:relative md:translate-x-0 shadow-2xl md:shadow-none",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          <div className="p-6 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="bg-primary-600 text-white p-2 rounded-xl shadow-lg">
                <SafeIcon icon={LuGraduationCap} size={20} />
              </div>
              <span className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Happynestling</span>
            </Link>
            <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 text-slate-400">
              <SafeIcon icon={LuX} size={20} />
            </button>
          </div>

          <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-200 text-sm",
                  isActive
                    ? item.ownerOnly
                      ? "bg-rose-600 text-white shadow-lg shadow-rose-500/20"
                      : "bg-primary-600 text-white shadow-lg shadow-primary-500/20"
                    : "text-slate-500 dark:text-slate-400 hover:bg-slate-50"
                )}
              >
                <SafeIcon icon={item.icon} size={18} />
                {item.label}
                {item.ownerOnly && (
                  <span className="ml-auto text-[7px] font-black uppercase bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full">
                    Owner
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 space-y-2 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-xl font-semibold text-slate-500 text-sm"
            >
              <SafeIcon icon={isDarkMode ? LuSun : LuMoon} size={18} />
              {isDarkMode ? 'Light' : 'Dark'}
            </button>
            <button
              onClick={async () => { await signOut(); navigate('/login'); }}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-xl font-semibold text-rose-600 text-sm"
            >
              <SafeIcon icon={LuLogOut} size={18} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Push content down when banner is showing */}
        {profile?.is_default_password && <div className="h-12 shrink-0" />}

        <header className="flex items-center justify-between px-4 md:px-8 py-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(true)} className="md:hidden p-2 text-slate-600 rounded-lg">
              <SafeIcon icon={LuMenu} size={20} />
            </button>
            <div className="hidden md:block"><Breadcrumbs /></div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-3 md:p-8 pb-32">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}